import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../features/cart/hooks/useCart';
import { useAuthContext } from '../../features/auth/context';
import { useMe } from '../../features/auth/hooks/useAuth';
import { useTheme } from '../theme/ThemeContext';
import DropLogo from './DropLogo';

/* ── Design tokens (estáticos) ───────────────────────────────── */
const fDisplay = "'Space Grotesk', system-ui, sans-serif";
const fBody    = "'Inter', system-ui, sans-serif";

const NAV_HEIGHT = 60;

/* ── Hook breakpoint ─────────────────────────────────────────── */
const useBreakpoint = () => {
  const [w, setW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { isMobile: w < 820 };
};

const NAV_LINKS = [
  { to: '/',            label: 'Inicio' },
  { to: '/catalogo',    label: 'Catálogo' },
  { to: '/mis-pedidos', label: 'Pedidos' },
];

/* ── Logo: paracaídas + Drop Hack ───────────────────────────── */
const Logo = ({ t }) => (
  <Link
    to="/"
    style={{
      textDecoration: 'none', flexShrink: 0,
      display: 'flex', alignItems: 'center', gap: 10,
    }}
  >
    <DropLogo size={42} />
    <span style={{
      fontFamily: fDisplay, fontWeight: 600, fontSize: 15,
      letterSpacing: '-0.02em', color: t.cream,
      lineHeight: 1,
    }}>
      Drop Hack
    </span>
  </Link>
);

/* ── Nav link ────────────────────────────────────────────────── */
const NavItem = ({ to, label, isActive, onClick, t }) => {
  const [hov, setHov] = useState(false);
  return (
    <Link
      to={to}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        fontFamily: fBody, fontSize: 14, fontWeight: 500,
        letterSpacing: '-0.005em',
        color: isActive ? t.cream : (hov ? t.cream : t.muted),
        textDecoration: 'none',
        padding: `0 16px`, height: NAV_HEIGHT,
        display: 'inline-flex', alignItems: 'center',
        position: 'relative',
        transition: 'color 0.2s',
      }}
    >
      {label}
      <span style={{
        position: 'absolute', left: 16, right: 16, bottom: -1,
        height: 2, background: t.accent,
        opacity: isActive ? 1 : 0,
        transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
        transformOrigin: 'left center',
        transition: 'opacity 0.2s, transform 0.2s',
      }} />
    </Link>
  );
};

/* ── Action button ───────────────────────────────────────────── */
const ActionBtn = ({ onClick, children, primary = false, t }) => {
  const [hov, setHov] = useState(false);
  const base = {
    fontFamily: fBody, fontSize: 13, fontWeight: 500,
    letterSpacing: '-0.005em',
    cursor: 'pointer',
    transition: 'all 0.2s',
    height: 34, padding: '0 18px',
    display: 'inline-flex', alignItems: 'center',
  };
  const style = primary
    ? {
        ...base,
        background: hov ? t.accent : t.cream,
        color: t.ink, border: 'none',
        fontWeight: 600,
      }
    : {
        ...base,
        background: 'transparent',
        color: hov ? t.cream : t.muted,
        border: 'none',
      };
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={style}
    >
      {children}
    </button>
  );
};

/* ── Shopping bag icon ───────────────────────────────────────── */
const BagIcon = ({ size = 19 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 7h14l-1.2 13.2a1 1 0 0 1-1 .8H7.2a1 1 0 0 1-1-.8L5 7z"/>
    <path d="M9 7V5.5a3 3 0 0 1 6 0V7"/>
  </svg>
);

/* ── Theme toggle button (sol/luna) ──────────────────────────── */
const ThemeToggle = ({ theme, onToggle, t }) => {
  const [hov, setHov] = useState(false);
  const isDark = theme === 'dark';
  return (
    <button
      onClick={onToggle}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      aria-label={isDark ? 'Activar tema claro' : 'Activar tema oscuro'}
      title={isDark ? 'Tema claro' : 'Tema oscuro'}
      style={{
        background: 'none', border: 'none',
        color: hov ? t.cream : t.muted,
        cursor: 'pointer', padding: 0,
        height: 36, width: 36,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'color 0.2s',
      }}
    >
      {isDark ? (
        /* Luna — visible cuando estás en oscuro (click para claro) */
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      ) : (
        /* Sol — visible cuando estás en claro (click para oscuro) */
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      )}
    </button>
  );
};

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useBreakpoint();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { theme, toggleTheme, tokens: t } = useTheme();

  const { logout } = useAuthContext();
  const { data: user } = useMe();
  const isLoggedIn = !!user;
  const username   = user?.username || localStorage.getItem('username') || '';
  const { cartItemCount } = useCart();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
    setMenuOpen(false);
  }, [logout, navigate]);

  useEffect(() => { if (!isMobile) setMenuOpen(false); }, [isMobile]);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const active = p => p === '/' ? location.pathname === '/' : location.pathname.startsWith(p);

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
        height: NAV_HEIGHT,
        background: scrolled ? t.navBgScrolled : t.navBg,
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        borderBottom: `1px solid ${scrolled ? t.hairlineStrong : t.hairline}`,
        transition: 'background 0.3s, border-color 0.3s',
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          padding: '0 clamp(16px, 4vw, 40px)',
          height: '100%',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 16,
        }}>

          <Logo t={t} />

          {!isMobile && (
            <div style={{
              display: 'flex', alignItems: 'center',
              position: 'absolute', left: '50%',
              transform: 'translateX(-50%)',
              height: '100%',
            }}>
              {NAV_LINKS.map(l => (
                <NavItem key={l.to} to={l.to} label={l.label} isActive={active(l.to)} t={t} />
              ))}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexShrink: 0 }}>

            {!isMobile && (
              isLoggedIn ? (
                <>
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%',
                    overflow: 'hidden', flexShrink: 0,
                    border: `1px solid ${t.hairlineStrong}`,
                  }}>
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={username}
                        referrerPolicy="no-referrer"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        width: '100%', height: '100%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: theme === 'light' ? 'rgba(10,10,11,0.08)' : 'rgba(245,245,240,0.08)',
                        fontFamily: fBody, fontSize: 11, fontWeight: 600, color: t.cream,
                      }}>
                        {username.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <span style={{
                    fontFamily: fBody, fontSize: 13, fontWeight: 400,
                    color: t.faint,
                  }}>
                    {username}
                  </span>
                  <ActionBtn t={t} onClick={handleLogout}>Salir</ActionBtn>
                </>
              ) : (
                <>
                  <ActionBtn t={t} onClick={() => navigate('/login')}>Login</ActionBtn>
                  <ActionBtn t={t} primary onClick={() => navigate('/login')}>Registro</ActionBtn>
                </>
              )
            )}

            <ThemeToggle theme={theme} onToggle={toggleTheme} t={t} />

            <Link
              to="/carrito"
              aria-label="Carrito"
              style={{
                position: 'relative', color: t.muted,
                display: 'flex', alignItems: 'center',
                textDecoration: 'none',
                transition: 'color 0.2s',
                height: 36, padding: '0 6px',
              }}
              onMouseEnter={e => e.currentTarget.style.color = t.cream}
              onMouseLeave={e => e.currentTarget.style.color = t.muted}
            >
              <BagIcon />
              {cartItemCount > 0 && (
                <span style={{
                  position: 'absolute', top: 4, right: -4,
                  background: t.accent, color: t.ink,
                  fontFamily: fBody, fontSize: 10, fontWeight: 700,
                  minWidth: 18, height: 18, borderRadius: 9,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 5px',
                  letterSpacing: 0,
                  border: `2px solid ${scrolled ? t.navBgScrolled : t.navBg}`,
                  boxSizing: 'content-box',
                }}>
                  {cartItemCount > 99 ? '99+' : cartItemCount}
                </span>
              )}
            </Link>

            {isMobile && (
              <button
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Menú"
                style={{
                  background: 'none', border: 'none', color: t.muted,
                  cursor: 'pointer', padding: 0, display: 'flex',
                  height: 36, width: 36, alignItems: 'center', justifyContent: 'center',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.color = t.cream}
                onMouseLeave={e => e.currentTarget.style.color = t.muted}
              >
                {menuOpen ? (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                    <line x1="3" y1="3" x2="15" y2="15"/>
                    <line x1="15" y1="3" x2="3" y2="15"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                    <line x1="3" y1="5" x2="15" y2="5"/>
                    <line x1="3" y1="13" x2="15" y2="13"/>
                  </svg>
                )}
              </button>
            )}
          </div>

        </div>
      </nav>

      <div style={{ height: NAV_HEIGHT }} />

      {isMobile && (
        <div style={{
          position: 'fixed', top: NAV_HEIGHT, left: 0, right: 0, zIndex: 199,
          background: theme === 'light' ? 'rgba(245,245,240,0.97)' : 'rgba(10,10,11,0.97)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? 'all' : 'none',
          transform: menuOpen ? 'translateY(0)' : 'translateY(-6px)',
          transition: 'opacity 0.2s, transform 0.2s',
          padding: '8px 0 24px',
          borderBottom: `1px solid ${t.hairlineStrong}`,
        }}>
          {NAV_LINKS.map(l => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMenuOpen(false)}
              style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between',
                fontFamily: fBody, fontSize: 16, fontWeight: 500,
                color: active(l.to) ? t.cream : t.muted,
                textDecoration: 'none',
                padding: '18px 24px',
                borderBottom: `1px solid ${t.hairline}`,
              }}
            >
              {l.label}
              {active(l.to) && (
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: t.accent,
                  boxShadow: `0 0 6px ${t.accent}80`,
                }} />
              )}
            </Link>
          ))}
          <div style={{ padding: '20px 24px 0', display: 'flex', gap: 10 }}>
            {!isLoggedIn ? (
              <>
                <button
                  onClick={() => { navigate('/login'); setMenuOpen(false); }}
                  style={{
                    flex: 1, fontFamily: fBody, fontSize: 14, fontWeight: 500,
                    color: t.cream, background: 'transparent',
                    border: `1px solid ${t.hairlineStrong}`,
                    padding: '14px 0', cursor: 'pointer',
                  }}
                >
                  Login
                </button>
                <button
                  onClick={() => { navigate('/login'); setMenuOpen(false); }}
                  style={{
                    flex: 1, fontFamily: fBody, fontSize: 14, fontWeight: 600,
                    background: t.cream, color: t.ink,
                    border: 'none',
                    padding: '14px 0', cursor: 'pointer',
                  }}
                >
                  Registro
                </button>
              </>
            ) : (
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', width: '100%',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    overflow: 'hidden', flexShrink: 0,
                    border: `1px solid ${t.hairlineStrong}`,
                  }}>
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={username}
                        referrerPolicy="no-referrer"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        width: '100%', height: '100%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: theme === 'light' ? 'rgba(10,10,11,0.08)' : 'rgba(245,245,240,0.08)',
                        fontFamily: fBody, fontSize: 10, fontWeight: 600, color: t.cream,
                      }}>
                        {username.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <span style={{ fontFamily: fBody, fontSize: 13, color: t.faint }}>{username}</span>
                </div>
                <button
                  onClick={handleLogout}
                  style={{
                    fontFamily: fBody, fontSize: 14, fontWeight: 500,
                    color: t.muted, background: 'none', border: 'none',
                    cursor: 'pointer', padding: 0,
                  }}
                >
                  Salir →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NavBar;
